import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { Button } from '@/components/button'
import { Field, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { platformAuditLogQuery } from '@/features/audit-log/api'
import { AuditEntryRow } from '@/features/audit-log/entry-row'
import { restaurantListQuery } from '@/features/restaurants/api'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'

/**
 * What platform users did to restaurants and accounts (I2), admin only. A
 * restaurant's own changes stay on its own page.
 */
export function PlatformAuditLogPage() {
  const t = useT()
  const navigate = useNavigate()
  const { page, restaurant } = useSearch({ from: '/platform-auth/audit-log' })
  const list = useQuery(platformAuditLogQuery(restaurant, page))
  // The first page of restaurants is the picker; a platform with more than a page of tenants is future work.
  const restaurants = useQuery(restaurantListQuery(1))
  const link = (page: number) => ({ to: '/audit-log', search: { page, ...(restaurant ? { restaurant } : {}) } }) as const
  useClampedPage(page, list.data, link)

  return (
    <Page width="lg" title={t('audit.platform.title')} className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t('audit.platform.intro')}</p>
      <Card className="grid gap-3 sm:grid-cols-2">
        <Field label={t('audit.platform.filter.restaurant')}>
          <Select
            value={restaurant ?? ''}
            onChange={(event) => {
              const value = event.target.value
              void navigate({ to: '/audit-log', search: value ? { page: 1, restaurant: value } : { page: 1 } })
            }}
          >
            <option value="">{t('audit.platform.allRestaurants')}</option>
            {restaurants.data?.items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </Field>
      </Card>

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t('audit.empty')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((entry) => (
              <li key={entry.id}>
                <AuditEntryRow entry={entry} />
              </li>
            ))}
          </ul>
        ))}
      <Pager label={t('audit.pager')} page={page} total={list.data?.total ?? 0} link={link} />
    </Page>
  )
}
